import { ReactNode, useEffect, useRef, useState } from "react";
import { CoursableProject, ProjectNote, ProjectWritingStyle } from "../../../../backend/Projects/types";
import { CreateNewNote, DeleteNote, FetchNotes, SaveNoteContent, SaveNoteName, StreamAIWriting } from "../../../../backend/Projects/ProjectNotes";
import { useNotifications } from "../../../../utils/NotificationsContext";

const cleanWritingOutput = (content: string) => content.replace(/【.*?】/g, "");

const useProjectNotes = (project: CoursableProject | undefined) => {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [writingToolStyle, setWritingToolStyle] = useState<AIWritingToolStyle | null>(null);
  const [writingTool, setWritingTool] = useState<AIWritingTool | null>(null);
  const [writingToolGenerating, setWritingToolGenerating] = useState(false);
  const [writingToolContent, setWritingToolContent] = useState("");
  const writingToolContext = useRef<string | undefined>(undefined);

  const initializingProjectID = useRef<string | undefined>(undefined);
  const creatingNewNote = useRef(false);
  const writingToolGeneratingAbortController = useRef<AbortController | undefined>(undefined);

  const { sendError } = useNotifications();

  async function Initialize(currentProject: CoursableProject) {
    setNotes([]);

    initializingProjectID.current = currentProject.id;
    const fetchedNotes = await FetchNotes(currentProject.id);
    if (initializingProjectID.current !== currentProject.id) return;

    if (fetchedNotes.length === 0 && !creatingNewNote.current) {
      creatingNewNote.current = true;
      fetchedNotes.push(await CreateNewNote(currentProject.id));
    }

    setNotes(fetchedNotes);
  }

  async function SaveContent(noteID: string, content: string) {
    if (!project) return;
    await SaveNoteContent(project.id, noteID, content);
    setNotes((previous) => previous.map((note) => (note.id === noteID ? { ...note, lastUpdated: new Date(), content } : note)));
  }

  async function SaveName(noteID: string, name: string) {
    if (!project) return;
    await SaveNoteName(project.id, noteID, name);
    setNotes((previous) => previous.map((note) => (note.id === noteID ? { ...note, lastUpdated: new Date(), name } : note)));
  }

  async function Delete(noteID: string) {
    if (!project) return;

    await DeleteNote(project.id, noteID);
    const remainingNotes = notes.filter((note) => note.id !== noteID);
    if (remainingNotes.length === 0) remainingNotes.push(await CreateNewNote(project.id));
    setNotes(remainingNotes);
  }

  async function SelectWritingTool(tool: AIWritingTool, context?: string) {
    if (!project) return;

    setWritingToolContent("");
    setWritingTool(tool);
    writingToolContext.current = context;
    if (!tool.requireUserPrompt) await UseWritingTool(tool);
  }

  async function UseWritingTool(tool: AIWritingTool, userPrompt?: string) {
    if (!project) return;

    const task = tool.prompt ?? userPrompt?.trim();
    if (!task) return;

    writingToolGeneratingAbortController.current?.abort();
    const abortController = new AbortController();
    writingToolGeneratingAbortController.current = abortController;
    setWritingToolGenerating(true);
    setWritingToolContent("");

    try {
      await StreamAIWriting(
        project.id,
        {
          task,
          ...(tool.prompt && userPrompt ? { userPrompt } : {}),
          ...(writingToolContext.current ? { context: writingToolContext.current } : {}),
          useMaterials: tool.useMaterials ?? false,
          writingStyle: writingToolStyle?.name ?? "none",
        },
        {
          onMessageDelta: (delta) => setWritingToolContent((previous) => cleanWritingOutput(previous + delta)),
          onMessageCompleted: (message) => setWritingToolContent(cleanWritingOutput(message.content)),
        },
        abortController.signal
      );
    } catch (error: any) {
      if (error.name !== "AbortError") sendError("Failed to use writing tool.", error.message || "Please try again later.");
    } finally {
      if (writingToolGeneratingAbortController.current === abortController) {
        writingToolGeneratingAbortController.current = undefined;
        setWritingToolGenerating(false);
      }
    }
  }

  function CancelWritingTool() {
    writingToolGeneratingAbortController.current?.abort();
    writingToolGeneratingAbortController.current = undefined;
    setWritingToolGenerating(false);
  }

  function DeselectWritingTool() {
    CancelWritingTool();
    setWritingTool(null);
    setWritingToolContent("");
    writingToolContext.current = undefined;
  }

  function SetWritingToolStyle(style: AIWritingToolStyle | null) {
    setWritingToolStyle(style);
  }

  useEffect(() => {
    setWritingTool(null);
    setWritingToolContent("");
    setWritingToolGenerating(false);
    writingToolContext.current = undefined;

    return () => {
      writingToolContext.current = undefined;
      creatingNewNote.current = false;
      const abortController = writingToolGeneratingAbortController.current;
      writingToolGeneratingAbortController.current = undefined;
      abortController?.abort();
    };
  }, [project?.id]);

  return {
    notes,
    writingToolStyle,
    writingTool,
    writingToolGenerating,
    writingToolContent,
    writingToolContext: writingToolContext.current,
    Initialize,
    SaveName,
    SaveContent,
    Delete,
    SetWritingToolStyle,
    SelectWritingTool,
    UseWritingTool,
    CancelWritingTool,
    DeselectWritingTool,
  };
};

export default useProjectNotes;

export interface AIWritingToolStyle {
  name: AIWritingStyleName;
  icon: ReactNode;
  color: string;
}

export type AIWritingStyleName = Exclude<ProjectWritingStyle, "none">;

export interface AIWritingTool {
  label: string;
  icon: ReactNode;
  prompt?: string;
  requireUserPrompt?: boolean;
  userPromptPlaceholder?: string;
  useMaterials?: boolean;
  replaceSelection?: boolean;
}
